import { useState, FormEvent } from 'react';
import { Download, CheckCircle, FileText, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  border: '1.5px solid var(--bw-border)',
  borderRadius: '10px',
  backgroundColor: 'var(--bw-white)',
  color: 'var(--bw-dark)',
  fontFamily: 'var(--font-body)',
  fontSize: '15px',
  outline: 'none',
  transition: 'border-color 0.15s',
};

const PDF_URL = 'https://brightwings-assets-prod.s3.us-east-1.amazonaws.com/guides/bw_10_questions.pdf';

const questions = [
  'Do you own your domain and understand what hosting actually means?',
  'Can you describe your users in one sentence — and who they are NOT?',
  'What does success look like at 90 days?',
  'Do you know the difference between an MVP and a full product?',
  'Have you validated this idea with real people?',
  'What happens if the tech goes down for 24 hours?',
  'Do you have a budget, or a "we\'ll figure it out" plan?',
  'Who approves decisions when you\'re not available?',
  'What\'s your data backup and ownership plan?',
  'Are you building for now, or trying to solve every future problem today?',
];

const DeveloperReady = () => {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const response = await fetch('/api/guide-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to submit');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero" style={{ paddingBottom: '40px' }}>
        <div className="max-w-5xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">Free Guide</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(32px, 5vw, 62px)', marginBottom: '24px' }}>
              Before you spend $10K on a developer —{' '}
              <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>answer these 10 questions first.</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 19px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '520px', margin: '0 auto 40px' }}>
              Most founders waste their first $20K. Not because they chose the wrong developer — because they weren't ready to hire one. This guide changes that.
            </p>
            <a
              href="#get-guide"
              className="bw-btn-primary"
              style={{ display: 'inline-flex', textDecoration: 'none' }}
            >
              <Download size={16} />
              Get the free guide
            </a>
          </div>
        </div>
      </section>

      {/* Questions + Form */}
      <section className="bw-section-surface">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            {/* Questions preview */}
            <div>
              <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '20px' }}>
                What's inside
              </p>
              <h2 style={{ ...hStyle, fontSize: 'clamp(24px, 3vw, 36px)', marginBottom: '32px' }}>
                10 questions your developer wishes you could already answer.
              </h2>
              <div className="space-y-4">
                {questions.map((q, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                      style={{ backgroundColor: 'var(--bw-blue-light)', color: 'var(--bw-blue)', fontFamily: 'var(--font-mono)' }}
                    >
                      {i + 1}
                    </span>
                    <p style={{ fontSize: '15px', color: 'var(--bw-text-mid)', lineHeight: 1.6 }}>{q}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Form card */}
            <div id="get-guide" style={{ scrollMarginTop: '100px' }}>
              <div className="bw-card" style={{ position: 'sticky', top: '100px' }}>
                {status === 'success' ? (
                  <div className="bw-card-body text-center py-10">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                      style={{ backgroundColor: 'var(--bw-blue-light)' }}
                    >
                      <CheckCircle size={32} color="var(--bw-blue)" />
                    </div>
                    <h3 style={{ ...hStyle, fontSize: '22px', marginBottom: '12px' }}>Check your inbox.</h3>
                    <p style={{ color: 'var(--bw-text-mid)', marginBottom: '28px', lineHeight: 1.6, fontSize: '15px' }}>
                      We sent the guide to <strong>{formData.email}</strong>. It should arrive in the next minute.
                    </p>
                    <a
                      href={PDF_URL}
                      download="BrightWings-10-Questions.pdf"
                      className="bw-btn-secondary"
                      style={{ display: 'inline-flex', textDecoration: 'none', gap: '8px', alignItems: 'center' }}
                    >
                      <FileText size={16} />
                      Download directly instead
                    </a>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="bw-card-body space-y-5">
                    <div>
                      <p style={{ ...hStyle, fontSize: '20px', marginBottom: '6px' }}>Get the free guide</p>
                      <p style={{ fontSize: '14px', color: 'var(--bw-muted)', lineHeight: 1.5 }}>
                        We'll email you the PDF instantly. No spam, ever.
                      </p>
                    </div>

                    {[
                      { id: 'name', label: 'First name *', type: 'text', placeholder: 'Alex' },
                      { id: 'email', label: 'Email *', type: 'email', placeholder: 'alex@yourcompany.com' },
                    ].map((field) => (
                      <div key={field.id}>
                        <label htmlFor={field.id} className="block text-sm font-medium mb-2" style={{ color: 'var(--bw-text-mid)' }}>
                          {field.label}
                        </label>
                        <input
                          type={field.type}
                          id={field.id}
                          name={field.id}
                          required
                          value={formData[field.id as keyof typeof formData]}
                          onChange={handleChange}
                          style={inputStyle}
                          placeholder={field.placeholder}
                          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--bw-blue)'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--bw-border)'; }}
                        />
                      </div>
                    ))}

                    {status === 'error' && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                        <p className="text-sm" style={{ color: '#DC2626' }}>
                          Something went wrong. You can also{' '}
                          <a href={PDF_URL} download style={{ color: 'var(--bw-blue)' }}>download the PDF directly</a>.
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="bw-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ width: '100%', justifyContent: 'center' }}
                    >
                      {status === 'submitting' ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Download size={16} />
                          Send me the guide
                        </span>
                      )}
                    </button>
                    <p className="text-xs text-center" style={{ color: 'var(--bw-muted)' }}>
                      Free. No spam. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="bw-hero" style={{ paddingTop: '80px', paddingBottom: '80px' }}>
        <div className="max-w-4xl mx-auto text-center">
          <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '20px' }}>
            Who this is for
          </p>
          <h2 style={{ ...hStyle, fontSize: 'clamp(24px, 3.5vw, 42px)', marginBottom: '20px' }}>
            Founder with a real idea — and no idea where to start technically.
          </h2>
          <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '560px', margin: '0 auto 48px' }}>
            This guide gives you the vocabulary and clarity to hire the right developer, scope your project honestly, and protect your budget.
          </p>
          <a
            href="#get-guide"
            className="bw-btn-primary"
            style={{ display: 'inline-flex', textDecoration: 'none' }}
          >
            Get the free guide
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* About BrightWings */}
      <section className="bw-section-surface">
        <div className="max-w-4xl mx-auto text-center">
          <p style={{ fontSize: '14px', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '520px', margin: '0 auto 16px' }}>
            This guide is from the team at{' '}
            <a href="/" style={{ color: 'var(--bw-blue)', textDecoration: 'none', fontWeight: 600 }}>BrightWings</a>
            {' '}— we help founders turn business pain into working software. Clear decisions first. Exact execution second.
          </p>
          <a href="/contact" style={{ fontSize: '14px', color: 'var(--bw-blue)', textDecoration: 'none', fontWeight: 600 }}>
            Want to talk through your project? →
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DeveloperReady;
